import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Reveal } from "../lib/motion";
import { useSlug } from "../components/ArticleLayout";
import { SERVICES, DELIVERY, IMAGES } from "../lib/data";
import { Check, ArrowUpRight, ArrowLeft } from "lucide-react";

const SERVICE_IMG = {
  "ai-transformation": IMAGES.aiBrain,
  "ai-solutions": IMAGES.aiBrain,
  "cybersecurity": IMAGES.security,
  "cloud-engineering": IMAGES.cloud,
  "data-engineering": IMAGES.dataCenter,
  "sap-implementation": IMAGES.building,
  "digital-transformation": IMAGES.team,
  "quality-assurance": IMAGES.dataCenter,
  "mobile-development": IMAGES.retail,
  "iot": IMAGES.cloud,
};

const OUTCOMES = [
  "Faster time-to-value — capability deployed in weeks",
  "Enterprise-grade governance, security and quality built in",
  "Measurable business outcomes agreed before work begins",
  "A durable partnership, not a one-off project",
];

export default function ServiceDetail() {
  const slug = useSlug();
  const s = SERVICES.find((x) => x.slug === slug);
  if (!s) {
    return (
      <div className="container-x flex min-h-[60vh] flex-col items-center justify-center pt-[72px] text-center">
        <h1 className="text-3xl font-semibold">Service not found</h1>
        <Link to="/services" className="mt-6 text-accent">All services</Link>
      </div>
    );
  }
  const Icon = s.icon;
  const img = SERVICE_IMG[s.slug] || IMAGES.aiBrain;

  return (
    <article>
      {/* Hero */}
      <section className="relative flex min-h-[68vh] items-end overflow-hidden pt-[72px]">
        <img src={img} alt={s.title} className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/70 to-navy/30" />
        <div className="container-x relative pb-16 text-white">
          <Reveal>
            <Link to="/services" className="mb-8 inline-flex items-center gap-2 text-sm text-white/70 transition-colors hover:text-accent">
              <ArrowLeft className="h-4 w-4" /> All services
            </Link>
            <span className="grid h-14 w-14 place-items-center rounded-xl brand-gradient text-white">
              <Icon className="h-6 w-6" />
            </span>
            <p className="mt-6 font-mono text-xs uppercase tracking-[0.28em] text-accent">{s.tag}</p>
            <h1 className="mt-3 max-w-3xl font-display text-4xl font-semibold tracking-tighter sm:text-5xl lg:text-6xl">{s.title}</h1>
          </Reveal>
        </div>
      </section>

      {/* Overview */}
      <section className="bg-background pt-20">
        <div className="container-x max-w-3xl">
          <Reveal>
            <p className="text-balance text-2xl font-light leading-relaxed sm:text-[28px]">{s.desc}</p>
          </Reveal>
        </div>
      </section>

      {/* What we deliver */}
      <section className="bg-background py-16">
        <div className="container-x grid gap-12 lg:grid-cols-2">
          <Reveal>
            <p className="overline text-gradient">What we deliver</p>
            <h2 className="mt-4 text-3xl font-semibold tracking-tight">Capabilities in this practice</h2>
            <div className="mt-8 grid gap-4">
              {s.points.map((p) => (
                <div key={p} className="flex items-start gap-3 rounded-xl border border-border bg-background p-4">
                  <span className="mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-full bg-accent/10 text-accent"><Check className="h-3.5 w-3.5" /></span>
                  <span className="font-medium">{p}</span>
                </div>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="overflow-hidden rounded-2xl border border-border shadow-xl">
              <motion.img src={img} alt={s.title} className="aspect-[4/3] w-full object-cover"
                initial={{ scale: 1.1 }} whileInView={{ scale: 1 }} viewport={{ once: true }} transition={{ duration: 1.2 }} />
            </div>
          </Reveal>
        </div>
      </section>

      {/* Approach */}
      <section className="border-y border-border bg-secondary/40 py-20">
        <div className="container-x">
          <Reveal className="mb-12 max-w-2xl">
            <p className="overline text-gradient">Our approach</p>
            <h2 className="mt-4 text-3xl font-semibold tracking-tight">How we deliver {s.title}</h2>
          </Reveal>
          <div className="grid gap-6 md:grid-cols-4">
            {DELIVERY.map((d, i) => {
              const DI = d.icon;
              return (
                <Reveal key={d.title} delay={i * 0.08} className="rounded-2xl border border-border bg-background p-6">
                  <DI className="h-6 w-6 text-accent" />
                  <p className="mt-4 font-mono text-xs text-muted-foreground">0{i + 1}</p>
                  <h3 className="mt-1 text-lg font-semibold tracking-tight">{d.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{d.desc}</p>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Outcomes */}
      <section className="bg-background py-20">
        <div className="container-x grid gap-10 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <p className="overline text-gradient">Outcomes</p>
            <h2 className="mt-4 text-3xl font-semibold tracking-tight">What you can expect</h2>
          </Reveal>
          <div className="lg:col-span-7">
            <div className="grid gap-4 sm:grid-cols-2">
              {OUTCOMES.map((o) => (
                <Reveal key={o} className="flex items-start gap-3">
                  <span className="mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-full bg-accent/10 text-accent"><Check className="h-3.5 w-3.5" /></span>
                  <span className="text-foreground/90">{o}</span>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden bg-navy py-20 text-white grain">
        <div className="container-x relative text-center">
          <Reveal>
            <h2 className="mx-auto max-w-2xl text-balance text-3xl font-semibold tracking-tighter sm:text-4xl">
              Ready to explore {s.title}?
            </h2>
            <Link to="/contact" className="mt-8 inline-flex items-center gap-2 rounded-full brand-gradient animate-gradient px-8 py-4 text-sm font-semibold brand-glow">
              Book a scoping call <ArrowUpRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>
    </article>
  );
}
